import type { ChatConversationPage, ChatSummary } from '@goblic/shared'
// Typed HTTP client for the Goblic API.
//
// Thin wrapper around fetch: attaches the bearer token, base URL and JSON
// (de)serialization. Resource-specific methods below are intentionally thin
// — they just shape the request/response types for each API route under
// packages/api/src/routes/**; no caching, retry or business logic lives here
// (caching/retry is TanStack Query's job on the caller side; validation and
// authorization are the API's job server-side).

import type { z } from 'zod'
import { buildWorkspaceNotice, setWorkspaceNotice } from './workspace-notice'
import {
  AuditLogEntrySchema,
  type Agent,
  type Approval,
  type ChatConversation,
  type ChatMessage,
  type ExecutorProfile,
  type McpAuthConfig,
  type McpService,
  type Project,
  type ProjectPolicy,
  type ProjectWorkflows,
  type RegistrySkill,
  type Role,
  type RoleRunSettings,
  type Run,
  type SandboxProfileType,
  type RunResult,
  type SignInSettings,
  type Skill,
  type SpecMode,
  type Task,
  type TaskRunStep,
  type TaskTransition,
  type CreateWorkflowTemplateRequest,
  type UpdateWorkflowTemplateRequest,
  type WorkflowTemplate,
  type WorkflowsUpdateRequest,
  type WorkflowUsage,
  type WorkflowValidationResult,
  type WorkflowVersionSummary,
  type AcceptInvitationBody,
  type CreateInvitationBody,
  type CreateInvitationResponse,
  type InvitationPreview,
  type InvitationOptions,
  type InvitationSummary,
  type Workspace,
  type WorkspaceDashboardSummary,
  type WorkspaceSummary,
  KB_CONFLICT_CODE,
  type UserRoleName,
  type KbConflictDetails,
  type KbFolderPolicy,
  type KbFolderPolicyListResponse,
  type KbMoveResult,
  type KbPage,
  type KbProposal,
  type KbProposalListResponse,
  type KbRevision,
  type KbRevisionListResponse,
  type KbRevisionStatus,
  type KbSearchResponse,
  type KbTreeResponse,
  type KbWriteResult,
  type ProjectReportListResponse,
  type ReportAssetUploadRequest,
  type ReportAttachResult,
  type ReportPutRequest,
  type ReportRenderResponse,
  type ReportOwnLatestSubmissionResponse,
  type ReportSubmissionListResponse,
  type ReportSubmitResult as ReportSubmitResponse,
  type ReportVersion,
  type ReportWriteResult,
  type TaskReportResponse,
  type ClearConnectionExhaustionResponse,
  type ConnectionExhaustedReason,
  type ConnectionExhaustion,
  type ConnectionModelTiers,
  type DecisionOverrideRequest,
  type HandoffRunRequest,
  type ResumeRunRequest,
  type RunRecoveryActionResponse,
  type RunRecoveryState,
  type BoardResponse,
  type CreateTaskLinkRequest,
  type CustomVars,
  type CustomVarsPatch,
  type MoveStreamRequest,
  type ProjectOverviewResponse,
  type PullResult,
  type QueueLane,
  type QueueMoveRequest,
  type Severity,
  type StreamDefinition,
  type StreamListResponse,
  type SubmitTaskRequest,
  type TaskLinkKind,
  type TaskLinksResponse,
  type TaskLinkView,
  type UpdateStreamRequest,
  type AgentPolicy,
  type CreateSandboxHostBody,
  type CreateTunnelSandboxHostResponse,
  type SandboxHostInstall,
  type InstallationInfo,
  type SandboxHost,
  type UpdateSandboxHostBody,
  type SubtaskBatchResult,
  type SubtaskDraftInput,
  type SubtasksResponse,
} from '@goblic/shared'
import { getAuthToken, getAuthWorkspaceId, setAuthToken, switchToToken } from './auth'
import { queryClient } from './query-client'
import { safeNextPath } from './safe-next'

export type AuditLogEntry = z.infer<typeof AuditLogEntrySchema>

/**
 * Where the API lives. Defaults to `/api` on the page's own origin (the Vite
 * dev server proxies it to the API, see vite.config.ts; in production the
 * reverse proxy does the same), so the app works from any host or device
 * without CORS. Set VITE_API_URL only to talk to an API elsewhere.
 */
export function getApiBaseUrl(): string {
  const configured = (
    import.meta.env.VITE_API_URL as string | undefined
  )?.trim()
  return (configured || `${window.location.origin}/api`).replace(/\/$/, '')
}

/** Thrown when the API can't be reached at all (as opposed to an error response). */
export class ApiUnreachableError extends Error {
  readonly reason: unknown

  constructor(reason: unknown) {
    super(`Can't reach the Goblic API at ${getApiBaseUrl()}.`)
    this.name = 'ApiUnreachableError'
    this.reason = reason
  }
}

/** Seconds from a Retry-After header (the delay form; an HTTP date is ignored). */
function parseRetryAfter(value: string | null): number | undefined {
  const seconds = value ? Number(value) : NaN
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined
}

/**
 * "Too many attempts. Try again in N minutes." for a 429 (rate limited), else
 * null so the caller falls back to its own message.
 */
export function rateLimitMessage(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 429) return null
  const seconds = error.retryAfterSeconds
  if (seconds === undefined) return 'Too many attempts. Try again in a few minutes.'
  if (seconds <= 60) return 'Too many attempts. Try again in a minute.'
  const minutes = Math.ceil(seconds / 60)
  return `Too many attempts. Try again in ${minutes} minutes.`
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: unknown
  /** Seconds the API asked us to wait (Retry-After on a 429). */
  readonly retryAfterSeconds?: number

  constructor(
    status: number,
    code: string,
    message: string,
    details?: unknown,
    retryAfterSeconds?: number
  ) {
    super(message)
    this.name = 'ApiError'
    this.retryAfterSeconds = retryAfterSeconds
    this.status = status
    this.code = code
    this.details = details
  }
}

export interface Paginated<T> {
  items: T[]
  nextCursor: string | null
}

type QueryValue = string | number | boolean | string[] | undefined

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Record<string, QueryValue>
  signal?: AbortSignal
  /** Send the stored bearer token (default). Off for sign-in itself, so a
   * stale token left over from an earlier session can't block logging in. */
  auth?: boolean
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  // Concatenate rather than resolve, so a base with a path (`…/api`) keeps it.
  const url = new URL(`${getApiBaseUrl()}${path}`)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined) continue
      url.searchParams.set(
        key,
        Array.isArray(value) ? value.join(',') : String(value)
      )
    }
  }
  return url.toString()
}

/**
 * The `details` of an API error (`{ error: { details } }`, errors/handler.ts):
 * e.g. WipLimitReachedDetails for `wip_limit_reached`, `{ failed }` for
 * `definition_of_ready_failed`. Undefined for anything that isn't an ApiError.
 */
export function apiErrorDetails<T>(error: unknown): T | undefined {
  if (!(error instanceof ApiError)) return undefined
  const body = error.details as
    | { error?: { details?: unknown }; details?: unknown }
    | undefined
  return (body?.error?.details ?? body?.details) as T | undefined
}

let recovering: Promise<void> | null = null

/**
 * The API answered 403 NOT_A_MEMBER: the session is fine but we were removed
 * from the open workspace. Fetches a token for another workspace of the same
 * session, leaves a notice for after the reload and switches to it. Falls
 * back to the sign-in page only if the session itself is gone.
 */
function recoverFromLostMembership(): Promise<void> {
  recovering ??= (async () => {
    const previousId = getAuthWorkspaceId()
    let previousName: string | null = null
    try {
      const cached = queryClient.getQueryData<{ id: string; name: string }[]>(['workspaces', 'mine'])
      previousName = cached?.find((w) => w.id === previousId)?.name ?? null
    } catch {
      previousName = null
    }
    try {
      const result = await apiFetch<{ token: string; user: CurrentUser }>(
        '/auth/recover-workspace',
        { method: 'POST' }
      )
      const next = result.user.workspaces?.find((w) => w.id === result.user.workspaceId)
      setWorkspaceNotice(buildWorkspaceNotice(previousName, next?.name))
      switchToToken(result.token, '/')
    } catch {
      // A 401 here was already handled (sign-in redirect) by apiFetch.
    }
  })()
  return recovering
}

/** Generic authenticated JSON request. Throws ApiError on a non-2xx response. */
export async function apiFetch<T>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const token = options.auth === false ? null : getAuthToken()
  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers: {
        // Only when there is a body: Fastify rejects an empty JSON body.
        ...(options.body !== undefined
          ? { 'Content-Type': 'application/json' }
          : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body:
        options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    })
  } catch (error) {
    // An aborted request is the caller's own doing; anything else means the
    // API didn't answer at all (down, wrong address, blocked by the network).
    if (error instanceof DOMException && error.name === 'AbortError')
      throw error
    throw new ApiUnreachableError(error)
  }

  // A token we sent was rejected: the session expired or was revoked. Drop
  // it and go back to sign-in instead of leaving every page erroring.
  if (response.status === 401 && token) {
    setAuthToken(null)
    if (!window.location.pathname.startsWith('/login')) {
      const back = window.location.pathname + window.location.search
      const next = safeNextPath(back)
      window.location.assign(
        `/login?expired=1${next ? `&next=${encodeURIComponent(next)}` : ''}`
      )
    }
  }

  // Valid session, but no longer a member of the workspace this token is for.
  if (response.status === 403 && token && path !== '/auth/recover-workspace') {
    const code = await response
      .clone()
      .json()
      .then((b: { error?: { code?: string }; code?: string }) => b?.error?.code ?? b?.code)
      .catch(() => undefined)
    if (code === 'NOT_A_MEMBER') {
      await recoverFromLostMembership()
      throw new ApiError(403, 'NOT_A_MEMBER', 'You no longer have access to this workspace', undefined)
    }
  }

  if (!response.ok) {
    // The API answers `{ error: { code, message, details } }`
    // (errors/handler.ts); a few routes answer `{ code, message }` flat.
    let details: unknown
    try {
      details = await response.json()
    } catch {
      details = undefined
    }
    type ErrorBody = {
      code?: string
      message?: string
      details?: { fieldErrors?: Record<string, string[] | undefined> }
    }
    const body = details as (ErrorBody & { error?: ErrorBody | string }) | undefined
    // The install routes answer a bare `{ error: 'enrollment_changed' }`.
    const bareCode = typeof body?.error === 'string' ? body.error : undefined
    const parsed = typeof body?.error === 'object' ? body.error : body
    // A failed validation reads better as its first field message.
    const fieldMessage = Object.values(parsed?.details?.fieldErrors ?? {}).find(
      (messages) => messages?.length
    )?.[0]
    throw new ApiError(
      response.status,
      parsed?.code ?? bareCode ?? 'unknown_error',
      fieldMessage ?? parsed?.message ?? response.statusText,
      details,
      parseRetryAfter(response.headers.get('Retry-After'))
    )
  }

  if (response.status === 204) {
    return undefined as T
  }
  return (await response.json()) as T
}

// ---------------------------------------------------------------------------
// Resource clients — one namespace per route module under packages/api/src/routes.
// ---------------------------------------------------------------------------

export interface CurrentUser {
  id: string
  email: string
  displayName: string
  workspaceId: string
  roles: string[]
  /** Null until the first-login welcome was finished or skipped. */
  onboardedAt: string | null
  /** Every workspace the user belongs to, by name (`GET /me`, login, switch). */
  workspaces?: WorkspaceSummary[]
  /** May manage installation-wide settings such as sandbox hosts (sandbox-hosts ontwerp §4). */
  isInstanceAdmin: boolean
}


export const authApi = {
  checkSetup: () =>
    apiFetch<{ needed: boolean }>('/auth/setup', { auth: false }),
  setup: (body: { email: string; name: string; password: string }) =>
    apiFetch<{
      token: string
      expiresAt: string
      user: CurrentUser
    }>('/auth/setup', { method: 'POST', body, auth: false }),
  login: (body: { email: string; password: string; workspaceId?: string }) =>
    apiFetch<{
      token: string
      expiresAt: string
      user: CurrentUser
    }>('/auth/login', { method: 'POST', body, auth: false }),
  logout: () => apiFetch<void>('/auth/logout', { method: 'POST' }),
  /** After losing the open workspace: a token for another one (same session). */
  recoverWorkspace: () =>
    apiFetch<{ token: string; expiresAt: string; user: CurrentUser }>(
      '/auth/recover-workspace',
      { method: 'POST' }
    ),
  /** A token for another workspace the user belongs to (same session). */
  switchWorkspace: (workspaceId: string) =>
    apiFetch<{ token: string; expiresAt: string; user: CurrentUser }>(
      '/auth/switch-workspace',
      { method: 'POST', body: { workspaceId } }
    ),
}

/**
 * EventSource/WebSocket give no status code on failure, so after a stream
 * error we ask a cheap authenticated endpoint; if that reveals a lost
 * membership or dead session, apiFetch handles it. Throttled.
 */
let lastProbe = 0
export function probeSessionAfterStreamError(): void {
  if (Date.now() - lastProbe < 10_000) return
  lastProbe = Date.now()
  void apiFetch('/me').catch(() => {})
}

export const meApi = {
  get: () => apiFetch<CurrentUser>('/me'),
  /** `done: true` when the welcome is finished or skipped. */
  setOnboarding: (done: boolean) =>
    apiFetch<CurrentUser>('/me/onboarding', { method: 'PUT', body: { done } }),
  /** How long a new sign-in lasts: the user's choice and what the installation allows. */
  getSignIn: () => apiFetch<SignInSettings>('/me/sign-in'),
  /** Null goes back to the installation default; applies from the next sign-in. */
  setSignIn: (sessionDurationSeconds: number | null) =>
    apiFetch<SignInSettings>('/me/sign-in', {
      method: 'PUT',
      body: { sessionDurationSeconds },
    }),
  /** Signs out every other session of this user; this one stays. */
  revokeOtherSessions: () =>
    apiFetch<{ revoked: number }>('/me/sessions/revoke-others', {
      method: 'POST',
    }),
}

/** Installation-wide settings; installation administrators only (sandbox-hosts ontwerp §5.2). */
export const installationApi = {
  get: () => apiFetch<InstallationInfo>('/installation'),
}

/** The Docker hosts run sandboxes start on. Secrets are write-only: responses only say whether one is set. */
export const sandboxHostsApi = {
  list: () => apiFetch<Paginated<SandboxHost>>('/installation/sandbox-hosts'),
  get: (id: string) => apiFetch<SandboxHost>(`/installation/sandbox-hosts/${id}`),
  create: (body: CreateSandboxHostBody) =>
    apiFetch<SandboxHost>('/installation/sandbox-hosts', { method: 'POST', body }),
  update: (id: string, body: UpdateSandboxHostBody) =>
    apiFetch<SandboxHost>(`/installation/sandbox-hosts/${id}`, { method: 'PATCH', body }),
  remove: (id: string) =>
    apiFetch<void>(`/installation/sandbox-hosts/${id}`, { method: 'DELETE' }),
  /** Queues a health check; the worker writes the result, so refetch after a moment. */
  check: (id: string) =>
    apiFetch<{ queued: true }>(`/installation/sandbox-hosts/${id}/check`, { method: 'POST' }),
  /** Queues "Scan & advise"; the worker stores the result in lastScan/lastScanAt, so poll get(). */
  scan: (id: string) =>
    apiFetch<{ queued: true }>(`/installation/sandbox-hosts/${id}/scan`, { method: 'POST' }),
  /** "Add host with installer": 201 with the host and its one-line install command (shown once). */
  createTunnel: (body: CreateSandboxHostBody) =>
    apiFetch<CreateTunnelSandboxHostResponse>('/installation/sandbox-hosts', { method: 'POST', body }),
  /** A new install token; the previous one stops working, the current enrollment stays until the script registers. */
  installToken: (id: string) =>
    apiFetch<SandboxHostInstall>(`/installation/sandbox-hosts/${id}/install-token`, { method: 'POST' }),
  /** `enrolledAt` is the one of the card the admin saw; 409 `enrollment_changed` when it is stale. */
  approveAgent: (id: string, enrolledAt: string) =>
    apiFetch<SandboxHost>(`/installation/sandbox-hosts/${id}/approve-agent`, {
      method: 'POST',
      body: { enrolledAt },
    }),
  /** Reject a pending agent or disconnect and revoke an approved one. */
  revokeAgent: (id: string) =>
    apiFetch<void>(`/installation/sandbox-hosts/${id}/revoke-agent`, { method: 'POST' }),
  updateAgent: (id: string) =>
    apiFetch<unknown>(`/installation/sandbox-hosts/${id}/update-agent`, { method: 'POST' }),
}

/** Installation-wide agent update policy (installer ontwerp §9.5). */
export const agentPolicyApi = {
  get: () => apiFetch<AgentPolicy>('/installation/agent-policy'),
  update: (autoUpdate: boolean) =>
    apiFetch<AgentPolicy>('/installation/agent-policy', { method: 'PUT', body: { autoUpdate } }),
}

/** A personal API token as listed; the token itself is only in `CreatedApiToken`. */
export interface ApiToken {
  id: string
  name: string
  scope: 'full' | 'read'
  /** The token's first characters, to recognise it. */
  prefix: string
  lastUsedAt: string | null
  expiresAt: string | null
  createdAt: string
}

export interface CreatedApiToken extends ApiToken {
  /** Shown once, right after creating it. */
  token: string
}

/** What an OAuth client asks for, shown on the consent page. */
export interface OAuthAuthorizationRequest {
  requestId: string
  clientName: string
  redirectHost: string
  scope: 'mcp' | 'mcp:read'
  expiresAt: string
  workspaces: { id: string; name: string; externalApiAccess: boolean }[]
}

/** An app the user has connected through OAuth (a grant, not a rotating token). */
export interface OAuthConnection {
  id: string
  clientName: string
  workspaceId: string
  workspaceName: string
  scope: 'mcp' | 'mcp:read'
  createdAt: string
  lastUsedAt: string | null
}

export const oauthApi = {
  getRequest: (id: string) =>
    apiFetch<OAuthAuthorizationRequest>(`/oauth/authorize/requests/${encodeURIComponent(id)}`),
  decide: (body: { requestId: string; workspaceId?: string; approve: boolean }) =>
    apiFetch<{ redirectUrl: string }>('/oauth/authorize/decision', {
      method: 'POST',
      body,
    }),
  listConnections: () =>
    apiFetch<{ data: OAuthConnection[] }>('/me/oauth-connections'),
  revokeConnection: (id: string) =>
    apiFetch<void>(`/me/oauth-connections/${id}`, { method: 'DELETE' }),
}

export const apiTokensApi = {
  list: () => apiFetch<{ data: ApiToken[] }>('/me/api-tokens'),
  create: (body: {
    name: string
    scope: 'full' | 'read'
    expiresInDays?: number | null
  }) =>
    apiFetch<CreatedApiToken>('/me/api-tokens', { method: 'POST', body }),
  revoke: (id: string) =>
    apiFetch<void>(`/me/api-tokens/${id}`, { method: 'DELETE' }),
}

export interface ListTasksParams {
  workspaceId: string
  projectId?: string
  status?: string
  source?: string
  workflowKey?: string
  queueLane?: QueueLane
  /** Only together with projectId. */
  ticketNo?: number
  /** JSON object string: tasks whose custom vars contain all of these (typed, exact). */
  customVars?: string
  /** Only the subtasks of this task. */
  parentTaskId?: string
  /** Only tasks that aren't subtasks. */
  topLevel?: boolean
  cursor?: string
  limit?: number
  [key: string]: QueryValue
}

export const tasksApi = {
  list: (params: ListTasksParams) =>
    apiFetch<Paginated<Task>>('/tasks', { query: params }),
  /** Tasks whose custom vars equal all of `vars` (e.g. { source_system: 'jira', external_id: 'ABC-12' }). */
  findByVars: (workspaceId: string, vars: CustomVars, projectId?: string) =>
    apiFetch<Paginated<Task>>('/tasks', {
      query: { workspaceId, projectId, customVars: JSON.stringify(vars) },
    }),
  get: (id: string) => apiFetch<Task>(`/tasks/${id}`),
  /** The task's subtasks with their progress summary. */
  subtasks: (id: string) => apiFetch<SubtasksResponse>(`/tasks/${id}/subtasks`),
  /** Creates (or, for an existing draft key, updates) one subtask draft. */
  createSubtask: (id: string, body: SubtaskDraftInput) =>
    apiFetch<SubtaskBatchResult>(`/tasks/${id}/subtasks`, { method: 'POST', body }),
  /** Deletes a subtask draft; the API refuses one that has started. */
  deleteSubtask: (id: string, subtaskId: string) =>
    apiFetch<void>(`/tasks/${id}/subtasks/${subtaskId}`, { method: 'DELETE' }),
  /**
   * Manual creation (plan §1 "Taken"). `acceptanceCriteria` left empty (or
   * omitted) is filled server-side from the project's Definition of Done;
   * `workflowKey` left null picks the project's default workflow at submit
   * time — omit it entirely for a project with no workflows configured.
   */
  createManual: (body: {
    workspaceId: string
    projectId: string
    title: string
    instructions: string
    acceptanceCriteria?: string[]
    riskLevel: Task['riskLevel']
    workflowKey?: string | null
    desiredRoleId?: string | null
    desiredAgentId?: string | null
    /** Defaults to the stream's entry lane. */
    queueLane?: QueueLane | null
    severity?: Severity | null
    customVars?: CustomVars
  }) => apiFetch<Task>('/tasks', { method: 'POST', body }),
  /**
   * Edits title/instructions/acceptance criteria/risk level/priority — the
   * fields a task's creator can revise after the fact (e.g. to lower a risk
   * level a role's ceiling rejected). 409s while the task has an active run
   * or open gate (routes/tasks/index.ts).
   */
  update: (
    id: string,
    body: {
      title?: string
      instructions?: string
      acceptanceCriteria?: string[]
      riskLevel?: Task['riskLevel']
      priority?: number
      severity?: Severity | null
      /** A null value deletes the key; see customVarsMode. */
      customVars?: CustomVarsPatch
      /** 'merge' (default) or 'replace' the whole map. */
      customVarsMode?: 'merge' | 'replace'
    }
  ) => apiFetch<Task>(`/tasks/${id}`, { method: 'PATCH', body }),
  /**
   * Validates preconditions and starts the task: through the workflow
   * engine when the project has workflows configured and no agent was
   * named directly, otherwise the legacy direct dispatch (one Run). There
   * is no separate manual-assign endpoint — the agent is always resolved
   * from the project's role/workflow policy (FD §16.3), never chosen by a
   * human directly. `run` is null for a workflow-started task: its first
   * step may be a human gate, or its run is created asynchronously.
   */
  submit: (id: string, body?: SubmitTaskRequest | object) =>
    apiFetch<{ task: Task; run: Run | null; workflow: WorkflowStepOutcome | null }>(
      `/tasks/${id}/submit`,
      {
        method: 'POST',
        // `object` keeps `useMutation({ mutationFn: tasksApi.submit })` compiling
        // (react-query passes its context as 2nd argument): only overrideWip is sent.
        body:
          (body as SubmitTaskRequest | undefined)?.overrideWip === true
            ? { overrideWip: true }
            : undefined,
      }
    ),
  /** Moves a queued task to another lane and/or position (409/422: wip_limit_reached, definition_of_ready_failed). */
  queue: (id: string, body: QueueMoveRequest) =>
    apiFetch<Task>(`/tasks/${id}/queue`, { method: 'POST', body }),
  /** Archive a finished task: it leaves the board and the default list (409 unless finished). */
  archive: (id: string) => apiFetch<Task>(`/tasks/${id}/archive`, { method: 'POST' }),
  unarchive: (id: string) => apiFetch<Task>(`/tasks/${id}/unarchive`, { method: 'POST' }),
  /** "Move to stream…": switches the task to another workflow's stream. */
  moveStream: (id: string, body: MoveStreamRequest) =>
    apiFetch<{ task: Task }>(`/tasks/${id}/move-stream`, { method: 'POST', body }),
  links: (id: string) => apiFetch<TaskLinksResponse>(`/tasks/${id}/links`),
  /** This task becomes the link's "from" (blocks / relates / duplicate_of / spawned_from `taskId`). */
  addLink: (id: string, body: CreateTaskLinkRequest) =>
    apiFetch<TaskLinkView>(`/tasks/${id}/links`, { method: 'POST', body }),
  removeLink: (id: string, kind: TaskLinkKind, otherTaskId: string) =>
    apiFetch<void>(`/tasks/${id}/links/${kind}/${otherTaskId}`, {
      method: 'DELETE',
    }),
  /** A task's workflow history (oldest first), empty for a legacy (non-workflow) task. */
  transitions: (id: string) =>
    apiFetch<TaskTransition[]>(`/tasks/${id}/transitions`),
  /** Every run of the task, oldest first — the same "steps so far" data a `workflow_gate` approval's `gateContext` uses, for the workflow-progress stepper. */
  steps: (id: string) =>
    apiFetch<{ steps: TaskRunStep[] }>(`/tasks/${id}/steps`),
  /**
   * Manually moves a task to another state of its workflow — allowed to any
   * state the workflow defines, including backwards; logged with cause
   * `manual` (plan §1, §2 WS2).
   */
  transition: (id: string, body: { toState: string; reason?: string }) =>
    apiFetch<WorkflowStepOutcome>(`/tasks/${id}/transition`, { method: 'POST', body }),
  /** Replaces the answer of the task's latest decision step and moves the task on from the chosen option (cause `manual`). */
  overrideDecision: (id: string, body: DecisionOverrideRequest) =>
    apiFetch<WorkflowStepOutcome>(`/tasks/${id}/decision-override`, { method: 'POST', body }),
}

/** Where a workflow task landed after a submit or move (API: workflow-engine.service.ts). */
export interface WorkflowStepOutcome {
  taskId: string
  workflowKey: string
  state: string
  status: Task['status']
  /** The run started (or already running) for an agent state. */
  runId: string | null
  /** The gate opened (or already open) for a human state. */
  approvalId: string | null
  /** Why the task is blocked, when it is. */
  blockedReason: string | null
  /** True when the move changed nothing. */
  ignored: boolean
}

export interface ListRunsParams {
  workspaceId?: string
  projectId?: string
  agentId?: string
  taskId?: string
  status?: string | string[]
  cursor?: string
  limit?: number
  [key: string]: QueryValue
}

export const runsApi = {
  list: (params: ListRunsParams) =>
    apiFetch<Paginated<Run>>('/runs', { query: params }),
  get: (id: string) => apiFetch<Run>(`/runs/${id}`),
  getResult: (id: string) => apiFetch<RunResult>(`/runs/${id}/result`),
  stop: (id: string, body: { reason?: string } = {}) =>
    apiFetch<Run>(`/runs/${id}/stop`, { method: 'POST', body }),
  pause: (id: string) => apiFetch<Run>(`/runs/${id}/pause`, { method: 'POST' }),
  retry: (id: string, body: { mode: 'same_snapshot' | 'current_config' }) =>
    apiFetch<Run>(`/runs/${id}/retry`, { method: 'POST', body }),
  /** Checkpoint, failure, handoff chain and the allowed recovery actions
   * (checkpoints contract §7). */
  recovery: (id: string) =>
    apiFetch<RunRecoveryState>(`/runs/${id}/recovery`),
  /** "Resume now" (no `at`) or "Resume at …" (`at`, ISO with offset). */
  resume: (id: string, body: ResumeRunRequest = {}) =>
    apiFetch<RunRecoveryActionResponse>(`/runs/${id}/resume`, {
      method: 'POST',
      body,
    }),
  /** "Hand off to …": a new run on another agent takes the work over. */
  handoff: (id: string, body: HandoffRunRequest) =>
    apiFetch<RunRecoveryActionResponse>(`/runs/${id}/handoff`, {
      method: 'POST',
      body,
    }),
  /**
   * SSE endpoint URL (FD §22.4, routes/events/sse.ts). SSE clients cannot set
   * an Authorization header, so the JWT is passed as `?token=` instead, and
   * `?lastEventId=` requests replay from that point per the server's
   * documented reconnect contract.
   */
  sseUrl: (runId: string, lastEventId?: string): string =>
    buildUrl(`/runs/${runId}/events`, {
      token: getAuthToken() ?? undefined,
      lastEventId,
    }),
}

/** An agent as the list returns it: plus its connection's usage-limit state
 * (checkpoints contract §7; optional until every API has it). */
export type AgentListItem = Agent & {
  connectionExhaustion?: ConnectionExhaustion | null
}

export const agentsApi = {
  remove: (id: string, workspaceId: string) =>
    apiFetch<unknown>(`/agents/${id}/permanent`, {
      method: 'DELETE',
      query: { workspaceId },
    }),
  list: (params: { workspaceId: string; projectId?: string }) =>
    apiFetch<Paginated<AgentListItem>>('/agents', { query: params }),
  get: (id: string, workspaceId: string) =>
    apiFetch<Agent>(`/agents/${id}`, { query: { workspaceId } }),
  create: (body: Partial<Agent> & { workspaceId: string }) =>
    apiFetch<Agent>('/agents', { method: 'POST', body }),
  update: (
    id: string,
    workspaceId: string,
    body: Partial<Agent> & { roleVersionId?: string }
  ) =>
    apiFetch<Agent>(`/agents/${id}`, {
      method: 'PATCH',
      body: { ...body, workspaceId },
    }),
}

export const approvalsApi = {
  list: (params: {
    workspaceId: string
    projectId?: string
    runId?: string
    /** A `workflow_gate` approval has no `runId`; filter by its task instead. */
    taskId?: string
    status?: string
  }) => apiFetch<Paginated<Approval>>('/approvals', { query: params }),
  get: (id: string) => apiFetch<Approval>(`/approvals/${id}`),
  approve: (id: string, body: { actionHash: string; note?: string }) =>
    apiFetch<Approval>(`/approvals/${id}/approve`, { method: 'POST', body }),
  reject: (id: string, body: { actionHash: string; note?: string }) =>
    apiFetch<Approval>(`/approvals/${id}/reject`, { method: 'POST', body }),
  instruct: (id: string, body: { actionHash: string; instruction: string }) =>
    apiFetch<Approval>(`/approvals/${id}/instruction`, {
      method: 'POST',
      body,
    }),
  /**
   * Reply to an `agent_question` approval (the `ask_human` run-tool) instead
   * of approve/reject: `answer` for a `'text'`/`'yes_no'` question, `choices`/
   * `other` for a `'single_choice'`/`'multiple_choice'` one (validated
   * server-side against the question's stored `options`), or `answers` —
   * one entry per sub-question, matched by `id`/`index` — for a
   * multi-question approval (the question's stored `questions` list).
   */
  answer: (
    id: string,
    body: {
      actionHash: string
      answer?: string
      choices?: string[]
      other?: string
      answers?: Array<{ id?: string; index?: number; answer?: string; choices?: string[]; other?: string }>
      comment?: string
    },
  ) => apiFetch<Approval>(`/approvals/${id}/answer`, { method: 'POST', body }),
}

export const chatApi = {
  listConversations: () =>
    apiFetch<Paginated<ChatConversation>>('/chat/conversations'),
  createConversation: (body: {
    credentialId: string
    title?: string
    model?: string
    confirmWrites?: boolean
  }) => apiFetch<ChatConversation>('/chat/conversations', { method: 'POST', body }),
  getConversation: (id: string, before?: string) =>
    apiFetch<ChatConversationPage>(`/chat/conversations/${id}`, { query: { before } }),
  getStatus: (id: string) => apiFetch<{ conversation: ChatConversation; summary: Pick<ChatSummary, 'throughSeq' | 'updatedAt'> | null }>(`/chat/conversations/${id}/status`),
  getMessage: (id: string, messageId: string) => apiFetch<ChatMessage>(`/chat/conversations/${id}/messages/${messageId}`),
  compact: (id: string) => apiFetch<{ ok: boolean }>(`/chat/conversations/${id}/compact`, { method: 'POST' }),
  updateConversation: (
    id: string,
    body: { title?: string; confirmWrites?: boolean }
  ) =>
    apiFetch<ChatConversation>(`/chat/conversations/${id}`, {
      method: 'PATCH',
      body,
    }),
  deleteConversation: (id: string) =>
    apiFetch<void>(`/chat/conversations/${id}`, { method: 'DELETE' }),
  /** Opens a turn and queues it; 202 while it's on the worker's queue. */
  postMessage: (
    id: string,
    body: { content: string; pageContext?: Record<string, unknown> }
  ) =>
    apiFetch<{ turnId: string }>(`/chat/conversations/${id}/messages`, {
      method: 'POST',
      body,
    }),
  confirm: (
    conversationId: string,
    messageId: string,
    body: { decision: 'confirm' | 'reject'; note?: string }
  ) =>
    apiFetch<ChatMessage>(
      `/chat/conversations/${conversationId}/confirmations/${messageId}`,
      { method: 'POST', body }
    ),
  /** Requests that the running turn be stopped (07 QA #5's stop button). */
  cancelTurn: (conversationId: string, turnId: string) =>
    apiFetch<{ ok: boolean }>(
      `/chat/conversations/${conversationId}/turns/${turnId}/cancel`,
      { method: 'POST' }
    ),
  /**
   * SSE endpoint URL (routes/chat/stream.ts) — same query-param JWT
   * convention as runsApi.sseUrl, since EventSource can't set headers.
   */
  streamUrl: (conversationId: string, lastEventId?: string): string =>
    buildUrl(`/chat/conversations/${conversationId}/stream`, {
      token: getAuthToken() ?? undefined,
      lastEventId,
    }),
}

export const auditApi = {
  list: (params: {
    workspaceId: string
    entityType?: string
    entityId?: string
    cursor?: string
    limit?: number
  }) => apiFetch<Paginated<AuditLogEntry>>('/audit-log', { query: params }),
}

/** The versioned part of a role: what an agent with this role is told and may do. */
export interface RoleVersionContent {
  baseInstructions: string
  skillIds: string[]
  allowedMcpServiceIds: string[]
  approvalRequiredActions: string[]
  workload: 'light' | 'heavy'
  /** What a run of this role may do beyond the built-in tools, and how much (05 §2.1). */
  runSettings: RoleRunSettings
}

export interface RoleVersionRecord extends RoleVersionContent {
  id: string
  roleId: string
  version: number
  isActive: boolean
  createdBy: string | null
  createdAt: string
}

export interface RoleSettings {
  name: string
  description?: string
  defaultExecutorType?: string
  defaultModel?: string
  allowedRiskLevel: 'low' | 'medium' | 'high' | 'critical'
}

export type RoleDetail = Role & {
  defaultExecutorType: string | null
  defaultModel: string | null
  defaultSandboxProfile: SandboxProfileType
  allowedRiskLevel: RoleSettings['allowedRiskLevel']
  current: RoleVersionRecord
}

export const rolesApi = {
  list: (params: { workspaceId: string }) =>
    apiFetch<Paginated<Role>>('/roles', { query: params }),
  get: (id: string, workspaceId: string) =>
    apiFetch<RoleDetail>(`/roles/${id}`, { query: { workspaceId } }),
  create: (body: RoleSettings & RoleVersionContent & { workspaceId: string }) =>
    apiFetch<unknown>('/roles', { method: 'POST', body }),
  /** Saves a new version; the old one stays in the history. */
  update: (
    id: string,
    body: Partial<RoleSettings & RoleVersionContent> & { workspaceId: string }
  ) => apiFetch<unknown>(`/roles/${id}`, { method: 'PATCH', body }),
  retire: (id: string, workspaceId: string) =>
    apiFetch<unknown>(`/roles/${id}`, {
      method: 'DELETE',
      query: { workspaceId },
    }),
  remove: (id: string, workspaceId: string) =>
    apiFetch<unknown>(`/roles/${id}/permanent`, {
      method: 'DELETE',
      query: { workspaceId },
    }),
  versions: (id: string, workspaceId: string) =>
    apiFetch<{ versions: RoleVersionRecord[] }>(`/roles/${id}/versions`, {
      query: { workspaceId },
    }),
  /** Makes a copy of an old version the newest one. */
  restore: (id: string, version: number, workspaceId: string) =>
    apiFetch<unknown>(`/roles/${id}/versions/${version}/restore`, {
      method: 'POST',
      body: { workspaceId },
    }),
}

export type SkillImportInput =
  | { source: 'local'; workspaceId: string; path: string }
  | {
      source: 'git'
      workspaceId: string
      url: string
      ref?: string
      subPath?: string
    }
  /** `id` is "owner/repo/skillId" as listed by skills.sh. */
  | { source: 'registry'; workspaceId: string; id: string }

export const skillsApi = {
  // NOTE: GET /skills replies `{ skills: [...] }`, not the `{ items,
  // nextCursor }` envelope most other list routes use (see
  // packages/api/src/routes/skills/index.ts) — unwrapped here so callers
  // still get the same Paginated<T> shape as every other *Api.list.
  list: async (params: { workspaceId: string }): Promise<Paginated<Skill>> => {
    const { skills } = await apiFetch<{ skills: Skill[] }>('/skills', {
      query: params,
    })
    return { items: skills, nextCursor: null }
  },
  import: (body: SkillImportInput) =>
    apiFetch<Skill>('/skills/import', { method: 'POST', body }),
  /** Searches skills.sh; the API needs at least 2 characters. */
  registrySearch: (q: string, limit?: number) =>
    apiFetch<{ skills: RegistrySkill[] }>('/skills/registry/search', {
      query: { q, limit },
    }),
  /** The most-installed skills on skills.sh (cached by the API). */
  registryPopular: () =>
    apiFetch<{ skills: RegistrySkill[] }>('/skills/registry/popular'),
  approve: (skillId: string, body: { actorId: string; note?: string }) =>
    apiFetch<Skill>(`/skills/${skillId}/approve`, { method: 'POST', body }),
  block: (skillId: string, body: { actorId: string; reason: string }) =>
    apiFetch<Skill>(`/skills/${skillId}/block`, { method: 'POST', body }),
  /** The skill's imported versions, newest first (what an agent can pin). */
  versions: (skillId: string) =>
    apiFetch<{ versions: { id: string; version: string; importedAt: string }[] }>(
      `/skills/${skillId}/versions`
    ),
}

/** What the MCP form sends; `secret` is stored encrypted and never returned. */
export interface McpServiceInput {
  name: string
  description: string | null
  transportType: 'http' | 'sse' | 'stdio'
  endpointOrCommand: string
  auth: McpAuthConfig | null
  /** On edit: leave out to keep the stored secret. */
  secret?: string
}

/** The outcome of connecting to a server and listing its tools. */
export interface McpCheckResult {
  status: 'healthy' | 'unhealthy' | 'unknown' | 'degraded'
  error: string | null
  latencyMs: number | null
  toolCount: number
}

export const mcpServicesApi = {
  list: (params: { workspaceId: string }) =>
    apiFetch<Paginated<McpService>>('/mcp-services', { query: params }),
  create: (body: McpServiceInput & { workspaceId: string }) =>
    apiFetch<McpService>('/mcp-services', { method: 'POST', body }),
  update: (id: string, body: Partial<McpServiceInput>) =>
    apiFetch<McpService>(`/mcp-services/${id}`, { method: 'PATCH', body }),
  remove: (id: string) =>
    apiFetch<void>(`/mcp-services/${id}`, { method: 'DELETE' }),
  /** Connects with the stored secret and refreshes the tool list. */
  check: (id: string) =>
    apiFetch<McpCheckResult>(`/mcp-services/${id}/health-probe`, {
      method: 'POST',
    }),
}

/** What a connection is for (the agents' executor types). `pi_login` is a
 * ChatGPT subscription login, run through a lightweight runner rather than
 * the Codex CLI; creatable only for a provider that passed the terms check
 * (docs/plans/2026-09-30-runners/03-pi-runner.md §2.6-2.9), today only
 * ChatGPT (`openai-codex`). */
export type ConnectionKind = 'claude_cli' | 'codex_cli' | 'openai_compatible' | 'pi_login'

export const CONNECTION_KIND_LABEL: Record<ConnectionKind, string> = {
  claude_cli: 'Claude Code',
  codex_cli: 'Codex',
  openai_compatible: 'OpenAI-compatible API',
  pi_login: 'ChatGPT subscription',
}

/** Which CLI a run uses; `agents.runner` picks one of a connection's
 * `allowedRunners` (see ExecutorConnection below). */
export type RunnerKind = 'claude-cli' | 'codex-cli' | 'pi'

/** A named login agents think with; several per kind are allowed. */
export interface ExecutorConnection {
  id: string
  workspaceId: string
  name: string
  kind: ConnectionKind
  /** Base URL, for an OpenAI-compatible API. */
  endpoint: string | null
  status: string
  agentCount: number
  /** Runners this login may pick between; first is its default
   * (docs/plans/2026-09-30-runners/03-pi-runner.md §2.6). */
  allowedRunners: RunnerKind[]
  /** For a `pi_login` connection: which subscription, e.g. `'openai-codex'`; null otherwise. */
  provider: string | null
  createdAt: string
  updatedAt: string
  lastVerifiedAt: string | null
  /** Usage limit hit: no new runs start on it until then (checkpoints
   * contract §3.4). Null/absent = usable. */
  exhaustedUntil?: string | null
  /** `auth` = needs a new login (no end time). */
  exhaustedReason?: ConnectionExhaustedReason | null
  /** Model per level (light/standard/heavy) for tasks a decision step set a level on; unset levels fall back (Claude: its defaults). */
  modelTiers?: ConnectionModelTiers | null
}

export interface ConnectionLogin {
  name?: string
  token?: string
  baseUrl?: string
  apiKey?: string
  /** For a `pi_login` connection: which subscription (only `'openai-codex'` today). */
  provider?: string
  /** Replaces the connection's model levels; a level left out is cleared. */
  modelTiers?: ConnectionModelTiers
}

export const connectionsApi = {
  list: (workspaceId: string) =>
    apiFetch<Paginated<ExecutorConnection>>('/executor-connections', {
      query: { workspaceId },
    }),
  create: (
    body: ConnectionLogin & { workspaceId: string; kind: ConnectionKind }
  ) =>
    apiFetch<ExecutorConnection>('/executor-connections', {
      method: 'POST',
      body,
    }),
  /** Rename, and/or replace the login. */
  update: (id: string, body: ConnectionLogin & { workspaceId: string }) =>
    apiFetch<ExecutorConnection>(`/executor-connections/${id}`, {
      method: 'PATCH',
      body,
    }),
  remove: (id: string, workspaceId: string) =>
    apiFetch<{ agentCount: number }>(`/executor-connections/${id}`, {
      method: 'DELETE',
      query: { workspaceId },
    }),
  /** Marks the connection usable again before its usage limit resets. */
  clearExhaustion: (id: string, workspaceId: string) =>
    apiFetch<ClearConnectionExhaustionResponse>(
      `/executor-connections/${id}/exhaustion/clear`,
      { method: 'POST', query: { workspaceId } }
    ),
  /** Begins a `pi_login` ChatGPT sign-in: open `url`, then pass the address
   * landed on and this same `state` to `piLoginFinish`. */
  piLoginStart: (workspaceId: string) =>
    apiFetch<{ url: string; state: string }>('/executor-connections/pi-login/start', {
      method: 'POST',
      body: { workspaceId },
    }),
  /** Finishes it: creates a connection, or (with `connectionId`) re-logs an
   * existing one in. */
  piLoginFinish: (body: {
    workspaceId: string
    state: string
    redirectUrl: string
    name?: string
    connectionId?: string
  }) =>
    apiFetch<ExecutorConnection>('/executor-connections/pi-login/finish', {
      method: 'POST',
      body,
    }),
  /** Models of one connection, or of the first connection of a kind. */
  models: (
    target: { connectionId: string } | { kind: string },
    workspaceId: string,
    refresh = false
  ) =>
    apiFetch<ExecutorModelList>(
      'connectionId' in target
        ? `/executor-connections/${target.connectionId}/models`
        : '/executor-connections/models',
      {
        query: {
          workspaceId,
          ...('kind' in target ? { kind: target.kind } : {}),
          ...(refresh ? { refresh: '1' } : {}),
        },
      }
    ),
}

export interface ExecutorModelList {
  executorType: string
  /** `builtin`: what the executor accepts, when the provider can't be asked (see `message`). */
  source: 'live' | 'builtin' | 'unavailable'
  models: {
    id: string
    name: string
    createdAt: string | null
    group?: string
  }[]
  /** Why there is no (full) list. */
  message?: string
  fetchedAt: string
}

/** A `web_search` tool provider (05-web-en-rolinstellingen.md §2.2). */
export type SearchProvider = 'searxng' | 'brave' | 'tavily' | 'exa'

export const SEARCH_PROVIDER_LABEL: Record<SearchProvider, string> = {
  searxng: 'SearXNG',
  brave: 'Brave Search',
  tavily: 'Tavily',
  exa: 'Exa',
}

/** A workspace's connection to a web-search provider. */
export interface SearchConnection {
  id: string
  workspaceId: string
  name: string
  provider: SearchProvider
  /** SearXNG only — its address, which may be on a private network. */
  baseUrl: string | null
  status: string
  createdAt: string
  updatedAt: string
  lastVerifiedAt: string | null
}

export interface SearchConnectionLogin {
  name?: string
  apiKey?: string
  /** SearXNG only. */
  baseUrl?: string
}

/** Just enough to populate `RoleForm`'s search-connection picker — no secrets, no addresses. */
export interface SearchConnectionOption {
  id: string
  name: string
  provider: SearchProvider
}

export const searchConnectionsApi = {
  list: (workspaceId: string) =>
    apiFetch<Paginated<SearchConnection>>('/search-connections', {
      query: { workspaceId },
    }),
  /** Names/ids/providers only — also reachable by someone who can edit roles but isn't an admin (05 QA finding #9). */
  options: (workspaceId: string) =>
    apiFetch<{ items: SearchConnectionOption[] }>('/search-connections/options', {
      query: { workspaceId },
    }),
  create: (
    body: SearchConnectionLogin & { workspaceId: string; provider: SearchProvider }
  ) =>
    apiFetch<SearchConnection>('/search-connections', {
      method: 'POST',
      body,
    }),
  /** Rename, and/or replace the key (and, for SearXNG, the address). */
  update: (id: string, body: SearchConnectionLogin & { workspaceId: string }) =>
    apiFetch<SearchConnection>(`/search-connections/${id}`, {
      method: 'PATCH',
      body,
    }),
  remove: (id: string, workspaceId: string) =>
    apiFetch<{ ok: true }>(`/search-connections/${id}`, {
      method: 'DELETE',
      query: { workspaceId },
    }),
  /** One proof search. */
  test: (id: string, workspaceId: string) =>
    apiFetch<{ ok: true; resultCount: number }>(`/search-connections/${id}/test`, {
      method: 'POST',
      query: { workspaceId },
    }),
}

export const executorsApi = {
  list: (params: { workspaceId: string }) =>
    apiFetch<Paginated<ExecutorProfile>>('/executors', { query: params }),
  /** The models this executor can use, asked from its provider with the workspace's login. */
  models: (type: string, workspaceId: string, refresh = false) =>
    apiFetch<ExecutorModelList>(`/executors/${type}/models`, {
      query: { workspaceId, ...(refresh ? { refresh: '1' } : {}) },
    }),
  /** Connect an OpenAI-compatible API; checked first by asking it for its models. */
  connectApi: (body: {
    workspaceId: string
    baseUrl: string
    apiKey?: string
  }) =>
    apiFetch<{ credentialId: string; baseUrl: string; modelCount: number }>(
      '/executors/openai_compatible_api/connection',
      { method: 'PUT', body }
    ),
  /** Store (or replace) an executor's login token; the value is never returned. */
  connectToken: (type: string, body: { workspaceId: string; token: string }) =>
    apiFetch<{ credentialId: string; status: string }>(
      `/executors/${type}/token`,
      { method: 'PUT', body }
    ),
}

export interface ProjectPolicyRecord {
  id: string
  projectId: string
  version: number
  policy: ProjectPolicy
  rawYaml: string | null
  checksum: string
  isActive: boolean
  createdBy: string | null
  createdAt: string
}

export interface PolicyValidationError {
  path: string
  message: string
}

export interface PolicyValidationResult {
  valid: boolean
  errors: PolicyValidationError[]
  policy?: ProjectPolicy
}

export const streamsApi = {
  list: (projectId: string) =>
    apiFetch<StreamListResponse>(`/projects/${projectId}/streams`),
  update: (projectId: string, streamKey: string, body: UpdateStreamRequest) =>
    apiFetch<StreamDefinition>(`/projects/${projectId}/streams/${streamKey}`, {
      method: 'PATCH',
      body,
    }),
  remove: (projectId: string, streamKey: string) =>
    apiFetch<void>(`/projects/${projectId}/streams/${streamKey}`, {
      method: 'DELETE',
    }),
  /** "Start next": pulls one task from the stream's queue right now. */
  startNext: (projectId: string, streamKey: string) =>
    apiFetch<PullResult>(`/projects/${projectId}/streams/${streamKey}/pull`, {
      method: 'POST',
    }),
}

export const boardApi = {
  get: (projectId: string, streamKey: string, opts?: { doneLimit?: number }) =>
    apiFetch<BoardResponse>(`/projects/${projectId}/board`, {
      query: { stream: streamKey, doneLimit: opts?.doneLimit },
    }),
  overview: (projectId: string) =>
    apiFetch<ProjectOverviewResponse>(`/projects/${projectId}/overview`),
  /** Project SSE URL (routes/events/project-sse.ts); the JWT goes in `?token=` like runsApi.sseUrl. */
  eventsUrl: (projectId: string): string =>
    buildUrl(`/projects/${projectId}/events`, {
      token: getAuthToken() ?? undefined,
    }),
}

/** Answer of `GET /projects/:id/openspec-status`. */
export type OpenspecStatus =
  | { state: 'present' | 'absent'; repo: string; ref: string }
  | { state: 'unavailable'; message: string }

export const projectsApi = {
  list: (params: { workspaceId: string; cursor?: string; limit?: number }) =>
    apiFetch<Paginated<Project>>('/projects', { query: params }),
  /** Every project of the workspace, following the cursor (200 per page, the API's max). */
  listAll: async (params: { workspaceId: string }): Promise<Project[]> => {
    const items: Project[] = []
    let cursor: string | undefined
    // Bounded so a misbehaving cursor can't loop forever.
    for (let page = 0; page < 50; page++) {
      const result = await apiFetch<Paginated<Project>>('/projects', {
        query: { workspaceId: params.workspaceId, cursor, limit: 200 },
      })
      items.push(...result.items)
      if (!result.nextCursor) break
      cursor = result.nextCursor
    }
    return items
  },
  /** Without `key` the server derives one from the name. */
  create: (body: { workspaceId: string; name: string; description: string; key?: string }) =>
    apiFetch<Project>('/projects', { method: 'POST', body }),
  update: (
    projectId: string,
    workspaceId: string,
    body: { name?: string; description?: string | null; key?: string; specMode?: SpecMode }
  ) =>
    apiFetch<Project>(`/projects/${projectId}`, {
      method: 'PATCH',
      body,
      query: { workspaceId },
    }),
  /** Whether the project's repository already has an `openspec/` folder (GitHub, default branch). */
  openspecStatus: (projectId: string, workspaceId: string) =>
    apiFetch<OpenspecStatus>(`/projects/${projectId}/openspec-status`, { query: { workspaceId } }),
  /** Permanent: removes the project with its tasks, runs, streams and settings. 409 while runs are in progress. */
  remove: (projectId: string, workspaceId: string) =>
    apiFetch<void>(`/projects/${projectId}`, { method: 'DELETE', query: { workspaceId } }),
  addRepository: (
    projectId: string,
    workspaceId: string,
    body: { url: string; defaultBranch: string; isDefault: boolean }
  ) =>
    apiFetch<Project>(`/projects/${projectId}/repositories`, {
      method: 'POST',
      body,
      query: { workspaceId },
    }),
  /** Per repository: how many OTHER projects (any workspace) share it. A count only. */
  repositoryLinks: (projectId: string) =>
    apiFetch<{ items: { url: string; otherProjectCount: number }[] }>(
      `/projects/${projectId}/repository-links`
    ),
  getPolicy: (projectId: string) =>
    apiFetch<ProjectPolicyRecord>(`/projects/${projectId}/policy`),
  /**
   * Dry-run validation returns 200 (valid) or 422 (invalid, with `errors`)
   * — both are legitimate results, not failures, so this resolves either
   * way instead of throwing on the 422 the way apiFetch normally would.
   */
  validatePolicy: async (
    projectId: string,
    yaml: string
  ): Promise<PolicyValidationResult> => {
    try {
      return await apiFetch<PolicyValidationResult>(
        `/projects/${projectId}/policy/validate`,
        {
          method: 'POST',
          body: { yaml },
        }
      )
    } catch (error) {
      if (error instanceof ApiError && error.status === 422 && error.details) {
        return error.details as PolicyValidationResult
      }
      throw error
    }
  },
  updatePolicy: (projectId: string, yaml: string) =>
    apiFetch<ProjectPolicyRecord>(`/projects/${projectId}/policy`, {
      method: 'PUT',
      body: { yaml },
    }),
}

export interface ProjectWorkflowsRecord extends ProjectWorkflows {
  /** Null while the project has no stored policy yet. */
  policyVersion: number | null
  /** Findings per workflow key, from `validateProjectWorkflows` (plan §1). */
  validation: Record<string, WorkflowValidationResult>
}

/** A template plus, when asked with a workspace, the roles it needs that the workspace doesn't have yet. */
export interface WorkflowTemplateOption extends WorkflowTemplate {
  missingRoles?: string[]
}

export const workflowsApi = {
  get: (projectId: string) =>
    apiFetch<ProjectWorkflowsRecord>(`/projects/${projectId}/workflows`),
  /**
   * Replaces every workflow of the project; bumps the policy version (plan
   * §1). `expectedPolicyVersion`/`migrations` (`WorkflowsUpdateRequest`,
   * `@goblic/shared`) let the editor detect a concurrent edit (409
   * `policy_version_conflict`) and move active tasks off a renamed/deleted
   * state in the same request.
   */
  update: (projectId: string, body: WorkflowsUpdateRequest) =>
    apiFetch<ProjectWorkflowsRecord>(`/projects/${projectId}/workflows`, {
      method: 'PUT',
      body,
    }),
  /** Active-task counts per state of one workflow, for the deleted-state migration dialog (plan §1.1/§7). */
  usage: (projectId: string, workflowKey: string) =>
    apiFetch<WorkflowUsage>(
      `/projects/${projectId}/workflows/${workflowKey}/usage`
    ),
  /** `workspaceId` also returns each template's `missingRoles` for that workspace. */
  templates: (workspaceId?: string) =>
    apiFetch<WorkflowTemplateOption[]>('/workflow-templates', {
      query: workspaceId ? { workspaceId } : undefined,
    }),
  /** Give `templateKey` for a built-in template, `templateId` for a workspace template (one of the two). */
  fromTemplate: (
    projectId: string,
    body: {
      templateKey?: string
      templateId?: string
      key?: string
      makeDefault?: boolean
      createMissingRoles?: boolean
    }
  ) =>
    apiFetch<ProjectWorkflowsRecord>(
      `/projects/${projectId}/workflows/from-template`,
      { method: 'POST', body }
    ),
  /** Every stored policy version of the project, newest first (compare versions). */
  versions: (projectId: string) =>
    apiFetch<WorkflowVersionSummary[]>(
      `/projects/${projectId}/workflows/versions`
    ),
  /** The workflows of one policy version (same envelope as `get`). */
  version: (projectId: string, version: number) =>
    apiFetch<WorkflowVersionRecord>(
      `/projects/${projectId}/workflows/versions/${version}`
    ),
  /** Saves a workspace template ("Save as template"); the key is derived from the name unless given. */
  createTemplate: (body: CreateWorkflowTemplateRequest) =>
    apiFetch<WorkflowTemplate>('/workflow-templates', { method: 'POST', body }),
  /** Workspace templates only (`source: 'workspace'`, by `id`). */
  updateTemplate: (id: string, body: UpdateWorkflowTemplateRequest) =>
    apiFetch<WorkflowTemplate>(`/workflow-templates/${id}`, {
      method: 'PUT',
      body,
    }),
  deleteTemplate: (id: string) =>
    apiFetch<void>(`/workflow-templates/${id}`, { method: 'DELETE' }),
}

/** `GET /projects/:id/workflows/versions/:version`. */
export interface WorkflowVersionRecord extends ProjectWorkflowsRecord {
  policyVersion: number
  isActive: boolean
  createdAt: string
  createdBy: string | null
}

/**
 * Redacted credential shape. The API's SELECT for this resource must never
 * include `ciphertext`/`iv`/`authTag` (see
 * packages/api/src/db/schema/credentials.ts) — this type intentionally has
 * no field for the secret value, and no UI built against it should add one.
 */
export interface CredentialSummary {
  id: string
  workspaceId: string
  projectId: string | null
  name: string
  type: string
  /** Non-secret targeting, e.g. `{ executorType }` or `{ provider: 'github' }`. */
  scope?: Record<string, unknown> | null
  status: string
  lastVerifiedAt: string | null
  createdAt: string
  /** Executor logins: usage limit hit (checkpoints contract §3.4). */
  exhaustedUntil?: string | null
  exhaustedReason?: ConnectionExhaustedReason | null
}

export interface ConnectSessionHandle {
  sessionId: string
  /** Relative websocket path the client attaches its terminal to (see
   * packages/api/src/routes/credentials/connect.ts). Turn it into a full
   * `ws(s)://` URL with `credentialsApi.attachWsUrl`. */
  attachUrl: string
  expiresAt: string
}

export interface ConnectHealthcheckResult {
  status: 'ok' | 'error'
  account: string | null
  lastCheckedAt: string
}

export const credentialsApi = {
  list: (params: { workspaceId: string }) =>
    apiFetch<Paginated<CredentialSummary>>('/credentials', { query: params }),
  create: (body: {
    workspaceId: string
    name: string
    type: string
    projectId?: string | null
    secretValue: string
  }) => apiFetch<CredentialSummary>('/credentials', { method: 'POST', body }),
  /** Connect or replace the workspace's GitHub token (push + draft pull requests). */
  connectGitHub: (body: { workspaceId: string; token: string }) =>
    apiFetch<CredentialSummary>('/credentials/github-token', {
      method: 'PUT',
      body,
    }),
  revoke: (id: string, workspaceId?: string) =>
    apiFetch<CredentialSummary>(`/credentials/${id}/revoke`, {
      method: 'POST',
      query: { workspaceId },
    }),
  /**
   * Starts the "Executor verbinden" controlled-terminal session (FD §9.3
   * step 2) for an existing credential. `executor` picks which CLI's
   * official login flow runs inside it (e.g. "claude-cli" | "codex-cli").
   * NOTE: the API route currently answers 501 — no PTY/websocket
   * provisioner is wired up yet (see connect.ts) — so callers must handle
   * that failure gracefully until that lands.
   */
  connectSession: (id: string, body: { executor: string }) =>
    apiFetch<ConnectSessionHandle>(`/credentials/${id}/connect/session`, {
      method: 'POST',
      body,
    }),
  /** Also currently 501 (no dispatch mechanism to the worker yet). */
  healthcheck: (id: string) =>
    apiFetch<ConnectHealthcheckResult>(
      `/credentials/${id}/connect/healthcheck`,
      { method: 'POST' }
    ),
  /**
   * Turns a session's relative `attachUrl` into a full `ws(s)://` URL the
   * browser can open a WebSocket against. A WebSocket handshake can't carry
   * an Authorization header, so — same as `runsApi.sseUrl` — the JWT rides
   * along as `?token=` instead.
   */
  attachWsUrl: (attachUrl: string): string => {
    const wsBase = getApiBaseUrl().replace(/^http/, 'ws')
    const url = new URL(`${wsBase}${attachUrl}`)
    const token = getAuthToken()
    if (token) url.searchParams.set('token', token)
    return url.toString()
  },
}

export interface GitHubInstallation {
  id: number
  account: string
  accountType: string
  repositorySelection: 'all' | 'selected'
  htmlUrl: string
}

export interface GitHubApp {
  credentialId: string
  slug: string
  owner: string
  htmlUrl: string
  installUrl: string
  installations: GitHubInstallation[]
}

export interface GitHubConnection {
  /** One App per organization or account. */
  apps: GitHubApp[]
  /** The first of `apps`. */
  app: GitHubApp | null
  token: { credentialId: string; connectedAt: string } | null
  permissions: Record<string, 'read' | 'write'>
}

/** "Connect with GitHub": a GitHub App created and installed through GitHub's own pages. */
export const githubApi = {
  status: (workspaceId: string) =>
    apiFetch<GitHubConnection>('/integrations/github', {
      query: { workspaceId },
    }),
  startManifest: (body: {
    workspaceId: string
    webOrigin: string
    organization?: string
  }) =>
    apiFetch<{ postUrl: string; manifest: string }>(
      '/integrations/github/manifest',
      {
        method: 'POST',
        body,
      }
    ),
  completeManifest: (body: {
    workspaceId: string
    code: string
    state: string
  }) =>
    apiFetch<{ installUrl: string }>('/integrations/github/manifest/complete', {
      method: 'POST',
      body,
    }),
  recordInstallation: (body: { workspaceId: string; installationId: number }) =>
    apiFetch<GitHubInstallation>('/integrations/github/installations', {
      method: 'POST',
      body,
    }),
  disconnectApp: (workspaceId: string, credentialId?: string) =>
    apiFetch<void>('/integrations/github/app', {
      method: 'DELETE',
      query: { workspaceId, credentialId },
    }),
  repos: (workspaceId: string) =>
    apiFetch<{ repos: { fullName: string }[] }>('/integrations/github/repos', {
      query: { workspaceId },
    }),
  branches: (workspaceId: string, repo: string) =>
    apiFetch<{ branches: { name: string }[] }>('/integrations/github/branches', {
      query: { workspaceId, repo },
    }),
}

/**
 * Mirrors packages/api/src/services/plane.service.ts's `PlaneTriggerCondition`/
 * `PlaneTriggerConfig` — duplicated here (rather than imported from the API
 * package) the same way the rest of this file only ever depends on
 * @goblic/shared, never on packages/api directly.
 */
export type PlaneTriggerCondition =
  | { field: 'project'; op: 'equals'; value: string }
  | { field: 'state'; op: 'equals'; value: string }
  | { field: 'label'; op: 'contains' | 'not_contains'; value: string }
  | { field: 'cycle'; op: 'is_set' | 'is_not_set' }
  | { field: 'approved_spec'; op: 'exists' }

export interface PlaneTriggerConfig {
  conditions: PlaneTriggerCondition[]
}

/** `GET /projects/:id/integrations/plane` response — never carries secret material (FD §19.1). */
export interface PlaneIntegrationStatus {
  configured: boolean
  isActive: boolean
  baseUrl: string | null
  workspaceSlug: string | null
  planeProjectId: string | null
  planeProjectName: string | null
  triggerConfig: PlaneTriggerConfig | null
  /** Goblic workflow state name -> Plane state name. */
  stateMapping: Record<string, string>
  /** Plane label name -> workflow key, e.g. `{ 'type:bug': 'bug' }`. */
  typeLabels: Record<string, string>
  webhookUrl: string | null
  hasApiToken: boolean
  hasWebhookSecret: boolean
}

export interface PlaneIntegrationTestResult {
  ok: boolean
  error?: string
  planeStates?: string[]
  planeLabels?: string[]
}

/** Body for `planeIntegrationApi.update` — omitting `apiToken`/`webhookSecret` leaves that half of the stored credential unchanged. */
export interface UpsertPlaneIntegrationBody {
  baseUrl: string
  workspaceSlug: string
  planeProjectId: string
  planeProjectName?: string | null
  triggerConfig: PlaneTriggerConfig
  stateMapping?: Record<string, string>
  typeLabels?: Record<string, string>
  apiToken?: string
  webhookSecret?: string
}

/** Project-level Plane integration: connect a Plane project, map its states/labels onto Goblic workflows (plan §1/§2 WS5). */
export const planeIntegrationApi = {
  get: (projectId: string) =>
    apiFetch<PlaneIntegrationStatus>(`/projects/${projectId}/integrations/plane`),
  update: (projectId: string, body: UpsertPlaneIntegrationBody) =>
    apiFetch<PlaneIntegrationStatus>(`/projects/${projectId}/integrations/plane`, {
      method: 'PUT',
      body,
    }),
  remove: (projectId: string) =>
    apiFetch<void>(`/projects/${projectId}/integrations/plane`, {
      method: 'DELETE',
    }),
  test: (projectId: string) =>
    apiFetch<PlaneIntegrationTestResult>(
      `/projects/${projectId}/integrations/plane/test`,
      { method: 'POST' }
    ),
}

export const workspacesApi = {
  listMine: () => apiFetch<WorkspaceSummary[]>('/me/workspaces'),
  /** Creates a workspace owned by the caller; the answer carries a token for it. */
  create: (body: { name: string; description?: string; timezone?: string }) =>
    apiFetch<{ workspace: Workspace; token: string; expiresAt: string; user: CurrentUser }>(
      '/workspaces',
      { method: 'POST', body }
    ),
  /** Leaves the workspace; the answer carries a token for another one of the caller's workspaces. */
  leave: (id: string) =>
    apiFetch<SessionResult>(`/workspaces/${encodeURIComponent(id)}/leave`, { method: 'POST' }),
  /** Deletes the workspace (owners only); `confirmName` must equal its name. */
  remove: (id: string, confirmName: string) =>
    apiFetch<SessionResult>(`/workspaces/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: { confirmName },
    }),
}

/** A fresh session for a workspace: what login, switch, accept, leave and delete answer. */
export interface SessionResult {
  token: string
  expiresAt: string
  user: CurrentUser
}

/** Roles a member or invitation can carry. */
export type MemberRole = UserRoleName

/** A workspace member as listed by `GET /workspaces/:id/users`. */
export interface WorkspaceMember {
  userId: string
  email: string
  displayName: string
  roles: MemberRole[]
  isActive?: boolean
  lastLoginAt?: string | null
  createdAt?: string
}

export const workspaceUsersApi = {
  list: (workspaceId: string) =>
    apiFetch<WorkspaceMember[]>(`/workspaces/${encodeURIComponent(workspaceId)}/users`),
  updateRoles: (workspaceId: string, userId: string, roles: MemberRole[]) =>
    apiFetch<WorkspaceMember>(
      `/workspaces/${encodeURIComponent(workspaceId)}/users/${encodeURIComponent(userId)}/role`,
      { method: 'PATCH', body: { roles } }
    ),
  remove: (workspaceId: string, userId: string) =>
    apiFetch<void>(
      `/workspaces/${encodeURIComponent(workspaceId)}/users/${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    ),
}

export const invitationsApi = {
  list: (workspaceId: string) =>
    apiFetch<InvitationSummary[]>(`/workspaces/${encodeURIComponent(workspaceId)}/invitations`),
  /** The link in `url` is the only place the token ever appears. */
  create: (workspaceId: string, body: CreateInvitationBody) =>
    apiFetch<CreateInvitationResponse>(
      `/workspaces/${encodeURIComponent(workspaceId)}/invitations`,
      { method: 'POST', body }
    ),
  /** Whether the server can e-mail invitations. */
  options: (workspaceId: string) =>
    apiFetch<InvitationOptions>(`/workspaces/${encodeURIComponent(workspaceId)}/invitations/options`),
  /** Replaces an open invitation by a fresh link (and e-mails it when mail is on). */
  resend: (workspaceId: string, invitationId: string) =>
    apiFetch<CreateInvitationResponse>(
      `/workspaces/${encodeURIComponent(workspaceId)}/invitations/${encodeURIComponent(invitationId)}/resend`,
      { method: 'POST' }
    ),
  revoke: (workspaceId: string, invitationId: string) =>
    apiFetch<void>(
      `/workspaces/${encodeURIComponent(workspaceId)}/invitations/${encodeURIComponent(invitationId)}`,
      { method: 'DELETE' }
    ),
  preview: (token: string) =>
    apiFetch<InvitationPreview>(`/auth/invitations/${encodeURIComponent(token)}`, { auth: false }),
  accept: (token: string, body: AcceptInvitationBody) =>
    apiFetch<SessionResult>(`/auth/invitations/${encodeURIComponent(token)}/accept`, {
      method: 'POST',
      body,
      auth: false,
    }),
}

export const workspaceApi = {
  get: (id: string) => apiFetch<Workspace>(`/workspaces/${id}`),
  update: (
    id: string,
    body: Partial<
      Pick<
        Workspace,
        'name' | 'description' | 'defaultApprovalPolicy' | 'externalApiAccess' | 'runCostDisplay'
      >
    >
  ) => apiFetch<Workspace>(`/workspaces/${id}`, { method: 'PATCH', body }),
  getDashboardSummary: (workspaceId: string) =>
    apiFetch<WorkspaceDashboardSummary>('/workspaces/dashboard-summary', {
      query: { workspaceId },
    }),
}

// ---------------------------------------------------------------------------
// Knowledge base (routes/knowledge/index.ts; contract in
// docs/plans/2026-10-02-kennisbank-contract.md §2). `space` is `workspace` or
// `project:<projectId>`; page paths are space-relative.
// ---------------------------------------------------------------------------

/** What `GET …/kb/runs/:runId/activity` answers (knowledge.service.ts `KbRunActivity`). */
export interface KbRunActivity {
  reads: { space: string; path: string; rev: number; pageId: string; at: string }[]
  writes: {
    space: string
    path: string
    status: 'applied' | 'proposed' | 'conflict'
    rev: number | null
    revisionId: string | null
    proposalId: string | null
    at: string
  }[]
}

function kbBase(workspaceId: string, space?: string): string {
  const root = `/workspaces/${encodeURIComponent(workspaceId)}/kb`
  return space ? `${root}/${encodeURIComponent(space)}` : root
}

export const kbApi = {
  /**
   * `seed: true` lets the API write the starter pages into a never-used
   * space (only for a caller with kb.write). Send it only when one specific
   * space is opened, never for an overview of all spaces.
   */
  tree: (workspaceId: string, space: string, options: { folder?: string; seed?: boolean } = {}) =>
    apiFetch<KbTreeResponse>(`${kbBase(workspaceId, space)}/tree`, {
      query: { folder: options.folder || undefined, seed: options.seed ? 'true' : undefined },
    }),
  getPage: (
    workspaceId: string,
    space: string,
    path: string,
    options: { includeDeleted?: boolean } = {}
  ) =>
    apiFetch<KbPage>(`${kbBase(workspaceId, space)}/pages`, {
      query: {
        path,
        includeDeleted: options.includeDeleted ? 'true' : undefined,
      },
    }),
  /** 409 `KB_CONFLICT` when `baseRev` is no longer current — see `kbConflictOf`. */
  putPage: (
    workspaceId: string,
    space: string,
    path: string,
    body: { content: string; message?: string; baseRev?: number }
  ) =>
    apiFetch<KbWriteResult>(`${kbBase(workspaceId, space)}/pages`, {
      method: 'PUT',
      query: { path },
      body,
    }),
  deletePage: (workspaceId: string, space: string, path: string, message?: string) =>
    apiFetch<KbWriteResult>(`${kbBase(workspaceId, space)}/pages`, {
      method: 'DELETE',
      query: { path, message: message || undefined },
    }),
  /** Page (`x.md`) or folder (`x/`) move within one space. */
  move: (workspaceId: string, space: string, body: { from: string; to: string }) =>
    apiFetch<KbMoveResult>(`${kbBase(workspaceId, space)}/pages/move`, {
      method: 'POST',
      body,
    }),
  /** "Still accurate": clears the stale flag and sets `verified_at`. */
  verify: (workspaceId: string, space: string, path: string, verifiedAt?: string) =>
    apiFetch<KbWriteResult>(`${kbBase(workspaceId, space)}/pages/verify`, {
      method: 'POST',
      query: { path },
      body: verifiedAt ? { verifiedAt } : {},
    }),
  revisions: (workspaceId: string, space: string, path: string) =>
    apiFetch<KbRevisionListResponse>(`${kbBase(workspaceId, space)}/pages/revisions`, {
      query: { path },
    }),
  revision: (workspaceId: string, space: string, revisionId: string) =>
    apiFetch<KbRevision>(
      `${kbBase(workspaceId, space)}/revisions/${encodeURIComponent(revisionId)}`
    ),
  restoreRevision: (workspaceId: string, space: string, revisionId: string, message?: string) =>
    apiFetch<KbWriteResult>(
      `${kbBase(workspaceId, space)}/revisions/${encodeURIComponent(revisionId)}/restore`,
      { method: 'POST', body: message ? { message } : {} }
    ),
  policies: (workspaceId: string, space: string) =>
    apiFetch<KbFolderPolicyListResponse>(`${kbBase(workspaceId, space)}/policies`),
  /** Replaces every stored policy of the space. */
  putPolicies: (workspaceId: string, space: string, policies: KbFolderPolicy[]) =>
    apiFetch<KbFolderPolicyListResponse>(`${kbBase(workspaceId, space)}/policies`, {
      method: 'PUT',
      body: { policies },
    }),
  search: (workspaceId: string, params: { q: string; space?: string; limit?: number }) =>
    apiFetch<KbSearchResponse>(`${kbBase(workspaceId)}/search`, { query: params }),
  proposals: (workspaceId: string, params: { status?: KbRevisionStatus; space?: string } = {}) =>
    apiFetch<KbProposalListResponse>(`${kbBase(workspaceId)}/proposals`, { query: params }),
  /** 409 `KB_CONFLICT` when the page moved on since the proposal's base — retry with `force`. */
  approveProposal: (
    workspaceId: string,
    revisionId: string,
    body: { note?: string; force?: boolean } = {}
  ) =>
    apiFetch<KbWriteResult>(
      `${kbBase(workspaceId)}/proposals/${encodeURIComponent(revisionId)}/approve`,
      { method: 'POST', body }
    ),
  rejectProposal: (workspaceId: string, revisionId: string, body: { note?: string } = {}) =>
    apiFetch<KbProposal>(
      `${kbBase(workspaceId)}/proposals/${encodeURIComponent(revisionId)}/reject`,
      { method: 'POST', body }
    ),
  runActivity: (workspaceId: string, runId: string) =>
    apiFetch<KbRunActivity>(`${kbBase(workspaceId)}/runs/${encodeURIComponent(runId)}/activity`),
}

/** The `KbConflictDetails` of a 409 `KB_CONFLICT`, or null for any other error. */
export function kbConflictOf(error: unknown): KbConflictDetails | null {
  if (!(error instanceof ApiError) || error.code !== KB_CONFLICT_CODE) return null
  // ApiError.details is the whole body: `{ error: { code, message, details } }`.
  const body = error.details as
    | { error?: { details?: unknown }; details?: unknown }
    | undefined
  const details = (body?.error?.details ?? body?.details) as
    | Partial<KbConflictDetails>
    | undefined
  if (!details || typeof details.currentRev !== 'number') return null
  return { currentRev: details.currentRev, current: details.current ?? null }
}

// ---------------------------------------------------------------------------
// Reports (routes/reports/index.ts, docs/plans/2026-10-02-rapporten-contract.md §2).
// The workspace comes from the token; ids from another workspace answer 404.
// ---------------------------------------------------------------------------

const enc = encodeURIComponent

export const reportsApi = {
  /** `report: null` when the task has no report (yet). */
  forTask: (taskId: string) =>
    apiFetch<TaskReportResponse>(`/tasks/${enc(taskId)}/report`),
  /** A person writes a new version (`report.write`). */
  put: (taskId: string, body: ReportPutRequest) =>
    apiFetch<ReportWriteResult>(`/tasks/${enc(taskId)}/report`, {
      method: 'PUT',
      body,
    }),
  /**
   * The complete iframe document. `assetBase` is always the API base URL
   * this browser uses, so the signed image URLs go through the same `/api`
   * proxy (contract §2) — without it they'd point at the API's own origin.
   */
  render: (reportId: string, version?: number) =>
    apiFetch<ReportRenderResponse>(`/reports/${enc(reportId)}/render`, {
      query: { version, assetBase: getApiBaseUrl() },
    }),
  version: (reportId: string, version: number) =>
    apiFetch<ReportVersion>(`/reports/${enc(reportId)}/versions/${version}`),
  uploadAsset: (reportId: string, body: ReportAssetUploadRequest) =>
    apiFetch<ReportAttachResult>(`/reports/${enc(reportId)}/assets`, {
      method: 'POST',
      body,
    }),
  submissions: (reportId: string) =>
    apiFetch<ReportSubmissionListResponse>(
      `/reports/${enc(reportId)}/submissions`
    ),
  /** The caller's own latest submission (for the report's `goblic.getLastSubmission()`). */
  latestOwnSubmission: (reportId: string) =>
    apiFetch<ReportOwnLatestSubmissionResponse>(
      `/reports/${enc(reportId)}/submissions/latest-own`
    ),
  submit: (reportId: string, body: { version: number; data: unknown }) =>
    apiFetch<ReportSubmitResponse>(`/reports/${enc(reportId)}/submissions`, {
      method: 'POST',
      body,
    }),
  forProject: (projectId: string) =>
    apiFetch<ProjectReportListResponse>(`/projects/${enc(projectId)}/reports`),
  /**
   * `GET /reports/:id/export` — the self-contained HTML (images inlined) as
   * a Blob plus the server's file name. Authenticated, so it can't be a plain
   * link; the caller turns the blob into an object URL in the parent page.
   */
  export: async (
    reportId: string,
    version?: number
  ): Promise<{ blob: Blob; filename: string | null }> => {
    const token = getAuthToken()
    let response: Response
    try {
      response = await fetch(
        buildUrl(`/reports/${enc(reportId)}/export`, { version }),
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      )
    } catch (error) {
      throw new ApiUnreachableError(error)
    }
    if (!response.ok) {
      let body: { error?: { code?: string; message?: string } } | undefined
      try {
        body = (await response.json()) as typeof body
      } catch {
        body = undefined
      }
      throw new ApiError(
        response.status,
        body?.error?.code ?? 'unknown_error',
        body?.error?.message ?? response.statusText,
        body
      )
    }
    const disposition = response.headers.get('Content-Disposition') ?? ''
    const match = /filename="([^"]+)"/.exec(disposition)
    return { blob: await response.blob(), filename: match?.[1] ?? null }
  },
}

// ---------------------------------------------------------------------------
// Wall dashboard (routes/wall on the API)
// ---------------------------------------------------------------------------

export const wallApi = {
  /** WebSocket URL for the read-only wall; the JWT rides in `?token=` like attachWsUrl. */
  wsUrl: (): string => {
    const url = new URL(`${getApiBaseUrl().replace(/^http/, 'ws')}/wall/ws`)
    const token = getAuthToken()
    if (token) url.searchParams.set('token', token)
    // "Today" on the wall is the screen's own day, not the server's.
    url.searchParams.set('tz', Intl.DateTimeFormat().resolvedOptions().timeZone)
    return url.toString()
  },
}
